<template>
  <section class="section-base form-section-base form-base">
    <div class="form-section-base__title">
      Сведения о заявителе, являющимся заинтересованным лицом
    </div>
    <v-container>
      <div class="form-section-base__controls">
        <v-row>
          <v-col>
            <v-radio-group v-model="role" row>
              <v-radio
                label="Физическое лицо"
                :value="Role.PERSON"
                :disabled="roleType !== Role.PERSON"
              />
              <v-radio
                label="Юридическое лицо"
                :value="Role.ORGANIZATION"
                :disabled="isOrganizationDisabled"
              />
              <v-radio
                label="Иностранное юридическое лицо"
                :value="Role.FOREIGN_ORGANIZATION"
                :disabled="isForeignOrganizationDisabled"
              />
            </v-radio-group>
          </v-col>
        </v-row>
      </div>

      <MrpaEnableTrusteePerson
        v-if="role === Role.PERSON"
        :v="$v.person"
        :disabled="disabled"
      />

      <MrpaEnableTrusteeOrganization
        v-if="role === Role.ORGANIZATION"
        :v="$v.organization"
        :disabled="disabled"
      />

      <MrpaEnableTrusteeForeignOrganization
        v-if="role === Role.FOREIGN_ORGANIZATION"
        :v="$v.foreignOrganization"
        :disabled="disabled"
      />

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';
import { Role } from '@/utils/dictionaries';
import { checkInvalid, leaveStep } from '@/utils/services';
import { ServicesModule } from '@/store/modules/services';
import Footer from '@/components/services/elements/Footer';
import { DocumentType, StepDirection, StepName } from '@/types/services';
import MrpaEnableTrusteePerson, {
  mrpaEnableTrusteePersonModel,
  getMrpaEnableTrusteePersonValidation,
  prefillEsiaMrpaEnableTrusteePerson,
} from './MrpaEnableTrusteePerson';
import MrpaEnableTrusteeOrganization, {
  getMrpaEnableTrusteeOrganizationValidation,
  mrpaEnableTrusteeOrganizationModel,
  prefillEsiaMrpaEnableTrusteeOrganization,
} from './MrpaEnableTrusteeOrganization';
import MrpaEnableTrusteeForeignOrganization, {
  getMrpaEnableTrusteeForeignOrganizationValidation,
  mrpaEnableTrusteeForeignOrganizationModel,
  prefillEsiaMrpaEnableTrusteeForeignOrganization,
} from './MrpaEnableTrusteeForeignOrganization';
import { prefillEsiaPersonTrustee } from '@/components/services/EgrnConfirm/Applicant/components/Person/PersonTrustee';

export default {
  name: 'MrpaEnableTrustee',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
  },
  components: {
    MrpaEnableTrusteePerson,
    MrpaEnableTrusteeOrganization,
    MrpaEnableTrusteeForeignOrganization,
    Footer,
  },
  data() {
    return {
      StepDirection,
      leaveStep,
      StepName,
      Role,
      role: this.roleType,
      isLoading: false,
      isLoaded: false,
      invalidFields: [],
      person: cloneDeep(mrpaEnableTrusteePersonModel),
      organization: cloneDeep(mrpaEnableTrusteeOrganizationModel),
      foreignOrganization: cloneDeep(mrpaEnableTrusteeForeignOrganizationModel),
      isFirstWatch: false,
    };
  },
  computed: {
    isOrganizationDisabled() {
      return this.roleType !== Role.ORGANIZATION;
    },

    isForeignOrganizationDisabled() {
      return ![Role.FOREIGN_ORGANIZATION, Role.ORGANIZATION].includes(
        this.roleType
      );
    },

    personIsTrustee() {
      return this.person.isTrustee;
    },
  },
  watch: {
    async personIsTrustee(isTrustee) {
      if (isTrustee) {
        if (this.isFirstWatch) {
          this.isFirstWatch = false;
          return;
        }

        const trustee = await prefillEsiaPersonTrustee();

        this.person = deepmerge(mrpaEnableTrusteePersonModel, {
          isTrustee,
          trustee,
        });
      } else {
        await this.prefillEsiaPerson();
      }
    },
  },
  validations() {
    return {
      person: getMrpaEnableTrusteePersonValidation(this.person),
      organization: getMrpaEnableTrusteeOrganizationValidation(
        this.organization
      ),
      foreignOrganization: getMrpaEnableTrusteeForeignOrganizationValidation(
        this.foreignOrganization
      ),
    };
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    touchFields() {
      this.invalidFields = [];

      this.$v[this.role].$touch();
      checkInvalid(this.$v[this.role], this.invalidFields);
    },

    async prefillDraft() {
      const applicantStore = cloneDeep(
        ServicesModule.statement.subjects.applicant
      );

      if (
        !applicantStore.person &&
        !applicantStore.organization &&
        !applicantStore.foreignOrganization
      ) {
        await this.prefillEsia();
        return;
      }

      if (applicantStore.person) {
        this.role = Role.PERSON;

        if (applicantStore.person.isTrustee) {
          this.isFirstWatch = true;
        }

        this.person = deepmerge.all([
          mrpaEnableTrusteePersonModel,
          applicantStore.person,
          {
            trustee:
              applicantStore.trustee ?? mrpaEnableTrusteePersonModel.trustee,
          },
        ]);
      }

      if (applicantStore.organization) {
        this.role = Role.ORGANIZATION;
        this.organization = deepmerge.all([
          mrpaEnableTrusteeOrganizationModel,
          applicantStore.organization,
          {
            trustee: applicantStore.trustee,
          },
        ]);
      }

      if (applicantStore.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.foreignOrganization = deepmerge.all([
          mrpaEnableTrusteeForeignOrganizationModel,
          applicantStore.foreignOrganization,
          {
            trustee: applicantStore.trustee,
          },
        ]);
      }
    },

    async prefillEsia() {
      if (this.roleType === Role.PERSON) {
        await this.prefillEsiaPerson();
      } else if (this.roleType === Role.ORGANIZATION) {
        await Promise.all([
          this.prefillEsiaOrganization(),
          this.prefillEsiaForeignOrganization(),
        ]);
      } else if (this.roleType === Role.FOREIGN_ORGANIZATION) {
        await this.prefillEsiaForeignOrganization();
      }
    },

    async prefillEsiaPerson() {
      this.person = await prefillEsiaMrpaEnableTrusteePerson();
    },

    async prefillEsiaOrganization() {
      this.organization = await prefillEsiaMrpaEnableTrusteeOrganization();
    },

    async prefillEsiaForeignOrganization() {
      this.foreignOrganization = await prefillEsiaMrpaEnableTrusteeForeignOrganization();
    },

    assemblyModel() {
      const applicant = {};

      applicant[this.role] = cloneDeep(this[this.role]);
      delete applicant[this.role].trustee;

      if ([Role.ORGANIZATION, Role.FOREIGN_ORGANIZATION].includes(this.role)) {
        applicant.trustee = cloneDeep(this[this.role]).trustee;

        if (
          applicant.trustee.documents.authorityDocument.type ===
          DocumentType.NOTARY_POWER_ATTORNEY
        ) {
          applicant.trustee.documents.authorityDocument.name = null;
        } else {
          applicant.trustee.documents.authorityDocument.regNumber = null;
        }

        return applicant;
      }

      if (this.person.isTrustee) {
        applicant.trustee = this.person.trustee;
      }

      return applicant;
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: StepName.S2,
      });
    },
  },
};
</script>
